import React, { useContext, useEffect, useState } from 'react';
import styles from './UserBasedOptions.module.scss';
import Select from 'react-select';
import { DropdownIndicator, reportAccordanceStyle } from '../../../../Filter/FilterComponent.Styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  getRegionsOptions,
  getCountriesOptions,
  getSectorOptions,
  getDivisionsOptions,
  getDivisionsIdsFromSelectedSectors,
  handleDefaultsSelectSelected,
  getDefaultCountry,
  getDefaultRegion,
  getDefaultDivision,
  getDefaultSector,
  handleUpdateItemsIds,
} from './Util';
import { ReportAccordanceContext } from '../ReportAccordance';
import Sector from '../../../../../models/Sector';
import Region from '../../../../../models/Region';
import SelectOption from '../../../../../models/SelectOption';
import { DefaultsSelects } from '../../../../../models/EDefaultsSelects';

const {
  label,
  formRightPanelContent,
  subTitleRightPanel

} = styles;
const selectFieldMap = {
  regions: 'regionId',
  countries: 'countryId',
  sectors: 'sectors',
  divisions: 'divisionId',
} as const;

const selectValueConverters: Record<keyof typeof selectFieldMap, (ids: number[], sourceItems: any[]) => SelectOption[]> = {
  regions: (ids: number[], regions: Region[]) =>
    ids
      .map(id => {
        const region = regions.find(item => item.id === id);
        return region
          ? {
            id: region.id,
            label: region.acronym,
            value: region.acronym,
          }
          : null;
      })
      .filter((option): option is SelectOption => Boolean(option)),
  countries: (ids: number[], regions: Region[]) => {
    const countries: SelectOption[] = [];
    regions.forEach(region => {
      region.countries.forEach(country => {
        if (ids.includes(country.id)) {
          countries.push({
            id: country.id,
            label: country.name,
            value: country.name,
            categoryId: country.regionId,
          });
        }
      });
    });
    return countries;
  },
  sectors: (ids: number[], sectors: Sector[]) =>
    ids
      .map(id => {
        const sector = sectors.find(item => item.id === id);
        return sector
          ? {
            id: sector.id,
            label: sector.description,
            value: sector.code,
          }
          : null;
      })
      .filter((option): option is SelectOption => Boolean(option)),
  divisions: (ids: number[], sectors: Sector[]) => {
    const divisions: SelectOption[] = [];
    sectors.forEach(sector => {
      sector.divisions.forEach(division => {
        if (ids.includes(division.id)) {
          divisions.push({
            id: division.id,
            label: division.name,
            value: division.name,
          });
        }
      });
    });
    return divisions;
  },
};

const extractIdsFromValues = (values: any[]): number[] =>
  values
    .map(value => {
      if (typeof value === 'number') return value;
      if (typeof value === 'object' && value !== null) {
        if ('id' in value && typeof (value as any).id === 'number') {
          return (value as any).id;
        }
        if ('value' in value && typeof (value as any).value === 'number') {
          return (value as any).value;
        }
      }
      return null;
    })
    .filter((id): id is number => typeof id === 'number');

const convertValuesToSelectOptions = (
  key: keyof typeof selectFieldMap,
  values: any[],
  sourceItems: Region[] | Sector[],
): SelectOption[] => {
  if (!values?.length) return [];
  const converter = selectValueConverters[key];
  if (!converter) return [];
  const ids = extractIdsFromValues(values);
  if (!ids.length) return [];
  return converter(ids, sourceItems);
};

export const UserBasedOptions = () => {
  const regions = useSelector<any, any>(state => state.regions);
  const menuTabs = useSelector<any, any>(state => state.menuTabs);
  const sectors = useSelector<any, Sector[]>(state => state.sectors);
  const filterIndicators = useSelector<any, any>(state => state.filterIndicators);
  const filter = useSelector<any, any>(state => state.filter);

  const { t } = useTranslation();

  const [selectedRegion, setSelectedRegion] = useState<any>([]);
  const [selectedCountry, setSelectedCountry] = useState<any>([]);
  const [selectedSector, setSelectedSector] = useState<any>([]);
  const [selectedDivision, setSelectedDivision] = useState<any>([]);

  const [countriesOptions, setCountriesOptions] = useState<any>(getCountriesOptions(regions, selectedRegion));
  const [divisionsOptions, setDivisionsOptions] = useState<any>(getDivisionsOptions(sectors, selectedSector));

  const [selectedParamsToEndPoint, setSelectedParamsToEndPoint] = useContext<any>(ReportAccordanceContext);

  useEffect(() => {
    const defaultSelects = handleDefaultsSelectSelected(menuTabs);

    const getSelected = (
      key: 'regions' | 'countries' | 'sectors' | 'divisions',
      defaultType: DefaultsSelects,
      getDefaultFn: (items: any[], defaults: any) => any,
      sourceItems: any[],
    ) => {
      const fieldKey = selectFieldMap[key];
      const commonIndicators = filterIndicators?.common ?? {};
      const commonFilter = filter?.common ?? {};

      const fromIndicators = convertValuesToSelectOptions(
        key,
        ((commonIndicators as any)[fieldKey] as any[]) ?? [],
        sourceItems,
      );
      if (fromIndicators.length) return fromIndicators;
      const fromFilter = convertValuesToSelectOptions(key, ((commonFilter as any)[fieldKey] as any[]) ?? [], sourceItems);
      if (fromFilter.length) return fromFilter;
      if (defaultSelects.default === defaultType) {
        const defaultOption = getDefaultFn(sourceItems, defaultSelects);
        return defaultOption ? [defaultOption] : [];
      }
      return [];
    };

    const initialRegion = getSelected('regions', DefaultsSelects.Region, getDefaultRegion, regions);
    setSelectedRegion(initialRegion);

    const initialCountry = getSelected('countries', DefaultsSelects.Country, getDefaultCountry, regions);
    setSelectedCountry(initialCountry);

    const initialSector = getSelected('sectors', DefaultsSelects.Sector, getDefaultSector, sectors);
    setSelectedSector(initialSector);

    const initialDivision = getSelected('divisions', DefaultsSelects.Division, getDefaultDivision, sectors);
    setSelectedDivision(initialDivision);

    setCountriesOptions(getCountriesOptions(regions, initialRegion));
    setDivisionsOptions(getDivisionsOptions(sectors, initialSector));
  }, [menuTabs, regions, sectors, filterIndicators, filter]);

  useEffect(() => {
    const portfolioSelectedTab = menuTabs?.portfolioSelectedTab;
    const tabRegionIds = portfolioSelectedTab?.regions ?? [];
    if (!tabRegionIds.length) {
      setSelectedRegion([]);
      setSelectedParamsToEndPoint(prev => ({
        ...prev,
        region: [],
      }));
      return;
    }

    const tabRegions = convertValuesToSelectOptions('regions', tabRegionIds, regions);
    setSelectedRegion(tabRegions);
    setSelectedParamsToEndPoint(prev => ({
      ...prev,
      region: handleUpdateItemsIds(tabRegions),
    }));
  }, [menuTabs, regions, setSelectedParamsToEndPoint]);

  useEffect(() => {
    const portfolioSelectedTab = menuTabs?.portfolioSelectedTab;
    const tabRegionIds = portfolioSelectedTab?.regions ?? [];
    if (!tabRegionIds.length) {
      setSelectedRegion([]);
      setSelectedParamsToEndPoint(prev => ({
        ...prev,
        region: [],
      }));
      return;
    }

    const tabRegions = convertValuesToSelectOptions('regions', tabRegionIds, regions);
    setSelectedRegion(tabRegions);
    setSelectedParamsToEndPoint(prev => ({
      ...prev,
      region: handleUpdateItemsIds(tabRegions),
    }));
  }, [menuTabs, regions, setSelectedParamsToEndPoint]);

  useEffect(() => {
    setCountriesOptions(getCountriesOptions(regions, selectedRegion));
    setSelectedParamsToEndPoint(prev => ({
      ...prev,
      region: handleUpdateItemsIds(selectedRegion),
    }));
  }, [selectedRegion, regions, setSelectedParamsToEndPoint]);

  useEffect(() => {
    setDivisionsOptions(getDivisionsOptions(sectors, selectedSector));
    setSelectedParamsToEndPoint(prev => ({
      ...prev,
      sector: handleUpdateItemsIds(selectedSector),
      division: Array.from(
        new Set([
          ...getDivisionsIdsFromSelectedSectors(sectors, selectedSector),
          ...handleUpdateItemsIds(selectedDivision),
        ]),
      ),
    }));
  }, [selectedSector, sectors, selectedDivision, setSelectedParamsToEndPoint]);

  useEffect(() => {
    setSelectedParamsToEndPoint(prev => ({
      ...prev,
      country: handleUpdateItemsIds(selectedCountry),
      division: handleUpdateItemsIds(selectedDivision),
    }));
  }, [selectedCountry, selectedDivision, setSelectedParamsToEndPoint]);

  useEffect(() => {
    const portfolioSelectedTab = menuTabs?.portfolioSelectedTab;
    if (!portfolioSelectedTab) return;

    const tabFilters = portfolioSelectedTab.filters || [];
    const getTabFilterValues = (field: string) => {
      const foundFilter = tabFilters.find(filter => filter.field === field);
      return foundFilter?.value ?? [];
    };

    const tabSectors = getTabFilterValues('SectorId');
    if (tabSectors.length) {
      const sectorOptions = convertValuesToSelectOptions('sectors', tabSectors, sectors);
      setSelectedSector(sectorOptions);
    }

    const tabDivisions = getTabFilterValues('DivisionId');
    if (tabDivisions.length) {
      const divisionOptions = convertValuesToSelectOptions('divisions', tabDivisions, sectors);
      setSelectedDivision(divisionOptions);
    }
  }, [menuTabs, sectors]);

  return (
    <>
      <div className={formRightPanelContent}>
        <p className={subTitleRightPanel}>{t('location_and_category')}</p>
        <div>
          <p className={label}>{t('region')}</p>
          <Select
            id="region"
            components={{ DropdownIndicator }}
            value={selectedRegion}
            options={getRegionsOptions(regions)}
            className="basic-multi-select"
            classNamePrefix="All"
            placeholder={t('all')}
            onChange={value => setSelectedRegion(value)}
            styles={reportAccordanceStyle}
            isMulti
          />
        </div>
        <div>
          <p className={label}>{t('country')}</p>
          <Select
            id="country"
            components={{ DropdownIndicator }}
            value={selectedCountry}
            options={countriesOptions}
            className="basic-multi-select"
            classNamePrefix="All"
            placeholder={t('all')}
            onChange={value => setSelectedCountry(value)}
            styles={reportAccordanceStyle}
            maxMenuHeight={150}
            isMulti
          />
        </div>
        <div>
          <p className={label}>{t('sector')}</p>
          <Select
            id="sector"
            components={{ DropdownIndicator }}
            value={selectedSector}
            options={getSectorOptions(sectors)}
            className="basic-multi-select"
            classNamePrefix="All"
            placeholder={t('all')}
            onChange={value => setSelectedSector(value)}
            styles={reportAccordanceStyle}
            isMulti
          />
        </div>
        <div>
          <p className={label}>{t('division')}</p>
          <Select
            id="division"
            components={{ DropdownIndicator }}
            value={selectedDivision}
            options={divisionsOptions}
            className="basic-multi-select"
            classNamePrefix="All"
            placeholder={t('all')}
            onChange={value => setSelectedDivision(value)}
            styles={reportAccordanceStyle}
            menuPlacement="top"
            isMulti
          />
        </div>
      </div>
    </>
  );
};


